import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getTenants } from '../../services/tenant.service';

const tenantLabel = (tenant) => {
  if (!tenant) return '';
  return [tenant.name, tenant.slug ? `(${tenant.slug})` : ''].filter(Boolean).join(' ');
};

export default function TenantPicker({
  value,
  onChange,
  selectedTenant = null,
  onTenantSelect,
  label = 'Tenant',
  required = false,
  placeholder = 'Search tenant by name, slug, email, or ID',
  helperText = '',
  excludePlatform = true,
  activeOnly = null,
  disabled = false,
}) {
  const [query, setQuery] = useState(tenantLabel(selectedTenant));
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const blurTimer = useRef(null);

  useEffect(() => {
    if (selectedTenant) {
      setQuery(tenantLabel(selectedTenant));
    } else if (!value) {
      setQuery('');
    }
  }, [selectedTenant, value]);

  useEffect(() => {
    if (!open || disabled) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const tenants = await getTenants(activeOnly, {
          q: query.trim(),
          limit: 25,
          excludePlatform,
        });
        if (!cancelled) {
          setOptions(Array.isArray(tenants) ? tenants : []);
        }
      } catch (error) {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeOnly, disabled, excludePlatform, open, query]);

  const selectedSummary = useMemo(() => {
    if (!selectedTenant) return null;
    const idShort = selectedTenant.id ? `${selectedTenant.id.slice(0, 8)}...` : '';
    return [
      selectedTenant.slug,
      selectedTenant.subscription_tier,
      selectedTenant.subscription_status,
      selectedTenant.is_active === false ? 'inactive' : 'active',
      idShort,
    ].filter(Boolean).join(' · ');
  }, [selectedTenant]);

  const pickTenant = (tenant) => {
    onChange?.(tenant.id);
    onTenantSelect?.(tenant);
    setQuery(tenantLabel(tenant));
    setOpen(false);
  };

  const clearTenant = () => {
    onChange?.('');
    onTenantSelect?.(null);
    setQuery('');
    setOptions([]);
    setOpen(true);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required ? ' *' : ''}
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) {
              onChange?.('');
              onTenantSelect?.(null);
            }
          }}
          onFocus={() => {
            if (blurTimer.current) clearTimeout(blurTimer.current);
            setOpen(true);
          }}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 180);
          }}
          className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-3 py-2 text-gray-900 dark:text-white disabled:opacity-60"
          placeholder={placeholder}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={clearTenant}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Clear
          </button>
        )}
      </div>

      {selectedTenant && (
        <div className="mt-2 rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-3 py-2">
          <div className="text-sm font-medium text-blue-900 dark:text-blue-100">{selectedTenant.name}</div>
          <div className="text-xs text-blue-700 dark:text-blue-300">{selectedSummary}</div>
        </div>
      )}

      {helperText && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{helperText}</p>
      )}

      {open && !disabled && (
        <div className="absolute z-[60] mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {loading && (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">Searching tenants...</div>
          )}
          {!loading && options.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
              No tenants found. Try name, slug, billing email, or tenant ID.
            </div>
          )}
          {!loading && options.map((tenant) => (
            <button
              type="button"
              key={tenant.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickTenant(tenant)}
              className="w-full border-b border-gray-100 dark:border-gray-700 px-3 py-2 text-left last:border-b-0 hover:bg-blue-50 dark:hover:bg-blue-900/30"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-900 dark:text-white">{tenant.name}</span>
                <span className={`text-xs ${tenant.is_active ? 'text-green-600 dark:text-green-300' : 'text-gray-500 dark:text-gray-400'}`}>
                  {tenant.is_active ? 'active' : 'inactive'}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {[tenant.slug, tenant.subscription_tier, tenant.subscription_status, tenant.id?.slice(0, 8)].filter(Boolean).join(' · ')}
              </div>
              {tenant.billing_email && (
                <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{tenant.billing_email}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
